import React, { useMemo, useState } from 'react';
import { Modal, Stack, Group, Text, TextInput, Button, Checkbox, NumberInput, Divider, Badge } from '@mantine/core';
import { DEFAULT_MAX_TIME_SCOPE_DAYS } from '../../services/optimization/constants';
import { clampRangeToMaxDays, getNextWeekMondayRange, toYmd } from '../../utils/date';
import { buildOptimizationModel } from '../../services/optimization/buildOptimizationModel';
import { buildSolverPayload } from '../../services/optimization/buildSolverPayload';
import { getPresetConfig } from '../../services/optimization/configPresets';
import useMembersManager from '../../hooks/useMembersManager';
import { db } from '../../firebase';
import { useTranslation } from 'react-i18next';

export default function OptimizationModal({
  opened,
  onClose,
  planningId,
  ownerId,
  assignableUsers = [],
  events = [],
  onRun,
  maxDays = DEFAULT_MAX_TIME_SCOPE_DAYS,
  presetKey = 'default',
  presetOverrides = {},
  weeklyDefaults = { internal: 40, external: 44 },
}) {
  const { t } = useTranslation('common');
  // Merge assignable users from props with live members (users + ghosts)
  const { participants: liveMembers } = useMembersManager({ db, planningId, currentUserId: null });
  const liveOptions = useMemo(() => (Array.isArray(liveMembers) ? liveMembers.map((p) => ({ value: p.uid, label: p.displayName || p.email || p.uid })) : []), [liveMembers]);
  const mergedAssignable = useMemo(() => {
    const map = new Map();
    for (const u of assignableUsers || []) map.set(u.value, u.label);
    for (const u of liveOptions || []) map.set(u.value, u.label);
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [assignableUsers, liveOptions]);
  // Default time scope: next week Monday -> Sunday
  const { start: defaultStart, end: defaultEnd } = useMemo(() => getNextWeekMondayRange(), []);
  const clampedDefault = useMemo(() => clampRangeToMaxDays(defaultStart, defaultEnd, maxDays), [defaultStart, defaultEnd, maxDays]);
  const [startDate, setStartDate] = useState(toYmd(clampedDefault.start));
  const [endDate, setEndDate] = useState(toYmd(clampedDefault.end));

  // Build participant list: everyone related to planning; owner present but unselected by default
  const initialPeople = useMemo(() => {
    const byUid = new Map((Array.isArray(liveMembers) ? liveMembers : []).map((m) => [m.uid, m]));
    const internalDefault = Number(weeklyDefaults?.internal) || 40;
    const externalDefault = Number(weeklyDefaults?.external) || 44;
    return mergedAssignable.map((u) => {
      const m = byUid.get(u.value);
      const personalized = Number(m?.weeklyHoursMax);
      const contract = m?.contract === 'external' ? 'external' : 'internal';
      const defaultByContract = contract === 'external' ? externalDefault : internalDefault;
      return {
        uid: u.value,
        label: u.label,
        selected: u.value !== ownerId,
        weeklyHoursMax: Number.isFinite(personalized) && personalized > 0 ? personalized : defaultByContract,
      };
    });
  }, [mergedAssignable, ownerId, liveMembers, weeklyDefaults]);
  const [people, setPeople] = useState(initialPeople);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    setPeople(initialPeople);
  }, [initialPeople]);

  const togglePerson = (uid, selected) => {
    setPeople((prev) => prev.map((p) => (p.uid === uid ? { ...p, selected } : p)));
  };

  const setHours = (uid, hours) => {
    // Keep raw input (string or number) to avoid unwanted auto-zeros while typing
    setPeople((prev) =>
      prev.map((p) => (p.uid === uid ? { ...p, weeklyHoursMax: hours } : p))
    );
  };

  const finalizeHours = (uid) => {
    setPeople((prev) =>
      prev.map((p) => {
        if (p.uid !== uid) return p;
        const n = Math.max(0, Math.min(168, Math.round(Number(p.weeklyHoursMax) || 0)));
        return { ...p, weeklyHoursMax: n };
      })
    );
  };

  const handleRun = async () => {
    if (submitting) return;
    setSubmitting(true);
    const key = presetKey || 'default';
    const config = getPresetConfig(key, presetOverrides || {});
    const optimizationFile = await buildOptimizationModel({
      planningId,
      startDate,
      endDate,
      people,
      events,
      maxDays,
      weeklyDefaults: { internal: Number(weeklyDefaults?.internal) || 40, external: Number(weeklyDefaults?.external) || 44 },
    });
    // Attach preset for downstream use
    optimizationFile.presetKey = key;
    optimizationFile.config = config;
    // Preview the solver payload in console for inspection
    buildSolverPayload(optimizationFile, config);
    try {
      await onRun?.(optimizationFile);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: snap to full weeks (Mon-Sun)
  const mondayOf = (d) => {
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return null;
    const dow = x.getDay();
    const diff = (dow + 6) % 7; // 0->6, 1->0, ...
    const m = new Date(x);
    m.setDate(x.getDate() - diff);
    m.setHours(0, 0, 0, 0);
    return m;
  };
  const sundayOf = (d) => {
    const m = mondayOf(d);
    if (!m) return null;
    const s = new Date(m);
    s.setDate(m.getDate() + 6);
    s.setHours(0, 0, 0, 0);
    return s;
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t('optimization.title')} size="lg">
      <Stack gap="md">
        <div>
          <Text fw={600} mb="xs">{t('optimization.time_scope')}</Text>
          <Group align="flex-end" gap="sm">
            <TextInput
              label={t('optimization.start_date')}
              type="date"
              value={startDate}
              onChange={(e) => {
                const v = e.currentTarget.value;
                if (!v) return;
                const sMon = mondayOf(v);
                if (!sMon) return;
                let newEnd = sundayOf(endDate || v) || sundayOf(v);
                if (newEnd.getTime() < sMon.getTime()) newEnd = sundayOf(sMon);
                setStartDate(toYmd(sMon));
                setEndDate(toYmd(newEnd));
              }}
            />
            <TextInput
              label={t('optimization.end_date')}
              type="date"
              value={endDate}
              onChange={(e) => {
                const v = e.currentTarget.value;
                if (!v) return;
                const eSun = sundayOf(v);
                if (!eSun) return;
                let sMon = mondayOf(startDate || v) || mondayOf(v);
                if (eSun.getTime() < sMon.getTime()) sMon = mondayOf(eSun);
                setStartDate(toYmd(sMon));
                setEndDate(toYmd(eSun));
              }}
            />
            <Badge variant="light">{t('optimization.max_days', { count: maxDays })}</Badge>
          </Group>
          <Text size="sm" c="dimmed" mt={6}>
            {t('optimization.full_weeks_hint', { defaultValue: 'Select complete weeks: start snaps to Monday, end snaps to Sunday.' })}
          </Text>
        </div>
        <Divider my="xs"/>
        <div>
          <Text fw={600} mb="xs">{t('optimization.participants')}</Text>
          <Stack gap="xs">
            {people.map((p) => (
              <Group key={p.uid} grow>
                <Checkbox
                  label={p.label}
                  checked={p.selected}
                  onChange={(e) => togglePerson(p.uid, e.currentTarget.checked)}
                />
                <NumberInput
                  label={t('optimization.weekly_hours_max')}
                  min={0}
                  max={168}
                  step={1}
                  value={p.weeklyHoursMax}
                  onChange={(val) => setHours(p.uid, val)}
                  onBlur={() => finalizeHours(p.uid)}
                  disabled={!p.selected}
                  hideControls
                />
              </Group>
            ))}
          </Stack>
        </div>
        
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleRun} loading={submitting} disabled={submitting}>{t('optimization.run')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
